<script setup lang="ts">
  import { type IPlanPriceHistory } from "~/entities/PlanPriceHistory";

  interface Props {
    planPrice?: IPlanPriceHistory;
    loading?: boolean;
    showDetail?: boolean;
    header?: boolean;
  }
  const localProps = defineProps<Props>();

  const emit = defineEmits(["onChange", "onHistory", "onDelete", "onCopyUrl"]);

  const billingType = computed<string>(() => {
    const type = getPlanBillingType(localProps.planPrice?.billingType ?? "", localProps.planPrice?.contractPeriod ?? 0);
    return type ? type.name : "-";
  });

  const popupMenuItems = [
    {
      text: "変更する",
      disabled: false,
      onClick: () => {
        emit(`onChange`, localProps.planPrice);
      },
    },
    // {
    //   text: "変更履歴",
    //   disabled: false,
    //   onClick: () => {
    //     emit(`onHistory`, localProps.planPrice);
    //   },
    // },
    {
      text: "削除する",
      disabled: localProps.planPrice?.isUndeletable,
      onClick: () => {
        if (localProps.planPrice?.isUndeletable) {
          return;
        }
        emit(`onDelete`, localProps.planPrice);
      },
    },
    {
      text: "URLをコピー",
      disabled: false,
      onClick: () => {
        emit(`onCopyUrl`, localProps.planPrice);
      },
    },
  ];

  // レスポンシブ対応
  const isTablet = isTabletAndSpDisplayRef();
</script>

<template>
  <div>
    <!-- draggableの子要素のrootは<div>にしないとエラーとなるのでここは変更しないこと -->
    <template v-if="!isTablet">
      <template v-if="header">
        <v-sheet class="columns" rounded color="transparent">
          <div class="col-handle"></div>
          <div class="col-name">プラン</div>
          <div class="col-billing-type">契約タイプ/金額</div>
          <div class="col-start-end-date">開始終了日</div>
          <div class="col-trial-days">無料</div>
          <div class="col-cumulative-member-count">累計入会数</div>
          <div class="col-withdraw-member-count">退会数</div>
          <div class="col-active-member-count">入会中</div>
          <div class="col-hidden">非表示</div>
          <div class="col-operation"></div>
        </v-sheet>
      </template>
      <template v-else-if="loading">
        <div class="my-4 d-flex">
          <div class="pa-0 flex-grow-1" align="left">
            <AtomsLoadingSkeleton class="rounded" :height="60" />
          </div>
        </div>
      </template>
      <template v-else>
        <v-sheet class="columns my-4 pt-4 pb-4 pr-4 pl-0" rounded color="monochrome-05">
          <!-- PC表示 -->
          <!-- 順番変更ボタン -->
          <div class="col-handle"><v-icon icon="$mdiSortVariant" class="handle" /></div>
          <div class="col-name text-value">
            <div>ID:{{ planPrice?.planPriceId }}</div>
            <div>{{ planPrice?.planPriceName }}</div>
          </div>
          <div class="col-billing-type text-value">
            <div>{{ billingType }}</div>
            <div>¥{{ numberFormat(planPrice?.price) }}</div>
          </div>
          <div class="col-start-end-date text-value">
            <div>{{ FormatDateString(planPrice?.startDate, "yyyy/MM/dd HH:mm") || "-" }}</div>
            <div>{{ FormatDateString(planPrice?.endDate, "yyyy/MM/dd HH:mm") || "-" }}</div>
          </div>
          <div class="col-trial-days text-value">
            {{ planPrice?.trialDays ? `${planPrice?.trialDays}日間` : "-" }}
          </div>
          <div class="col-cumulative-member-count text-value">
            {{ numberFormat(planPrice?.cumulativeMembershipCount) }}
          </div>
          <div class="col-withdraw-member-count text-value">
            {{ numberFormat(planPrice?.withdrawMembershipCount) }}
          </div>
          <div class="col-active-member-count text-value">
            {{ numberFormat(planPrice?.activeMembershipCount) }}
          </div>
          <div class="col-hidden text-value">{{ planPrice?.displayFlag ? "-" : "非表示" }}</div>
          <div class="col-operation text-value">
            <v-menu scroll-strategy="close">
              <template #activator="{ props }">
                <v-icon v-bind="props" icon="$mdiDotsVertical" />
              </template>
              <v-sheet class="popup">
                <div class="popup-list">
                  <div
                    v-for="(item, index) in popupMenuItems"
                    :key="index"
                    :class="item.disabled ? 'popup-list-item-disabled' : 'popup-list-item'"
                    @click="item.onClick"
                  >
                    <div class="popup-text px-4" :class="{ disabled: item.disabled }">
                      {{ item.text }}
                    </div>
                  </div>
                </div>
              </v-sheet>
            </v-menu>
          </div>
        </v-sheet>
      </template>
    </template>
    <div v-else class="mx-0 my-4">
      <!-- SP表示 -->
      <v-sheet class="columns-sp pa-4" rounded color="monochrome-05">
        <!-- 順番変更ボタン -->
        <div class="col-handle-sp"><v-icon icon="$mdiSortVariant" class="handle" /></div>
        <div class="col-name-sp text-value">{{ planPrice?.planPriceName }}</div>
        <div class="col-operation text-value">
          <v-menu scroll-strategy="close">
            <template #activator="{ props }">
              <v-icon v-bind="props" icon="$mdiDotsVertical" />
            </template>
            <v-sheet class="popup">
              <div class="popup-list">
                <div
                  v-for="(item, index) in popupMenuItems"
                  :key="index"
                  :class="item.disabled ? 'popup-list-item-disabled' : 'popup-list-item'"
                  @click="item.onClick"
                >
                  <div class="popup-text px-4" :class="{ disabled: item.disabled }">
                    {{ item.text }}
                  </div>
                </div>
              </div>
            </v-sheet>
          </v-menu>
        </div>
      </v-sheet>
      <v-sheet v-if="showDetail" color="monochrome-05 px-4">
        <v-divider />
      </v-sheet>
      <v-sheet v-if="showDetail" class="columns-sp pa-4" rounded color="monochrome-05">
        <div class="col-price-id-sp">
          <p class="text-title">プランID</p>
          <p class="text-value">{{ planPrice?.planPriceId }}</p>
        </div>
        <div class="col-billing-type-sp">
          <p class="text-title">契約タイプ/金額</p>
          <p class="text-value">{{ billingType }}</p>
          <p class="text-value">¥{{ numberFormat(planPrice?.price) }}</p>
        </div>
        <div class="col-start-end-date">
          <p class="text-title">開始終了日</p>
          <p class="text-value">
            {{ FormatDateString(planPrice?.startDate, "yyyy/MM/dd HH:mm") || "-" }}
            <br />
            {{ FormatDateString(planPrice?.endDate, "yyyy/MM/dd HH:mm") || "-" }}
          </p>
        </div>
        <div class="col-trial-days">
          <p class="text-title">無料</p>
          <p class="text-value">
            {{ planPrice?.trialDays ? `${planPrice?.trialDays}日間` : "-" }}
          </p>
        </div>
        <div class="col-cumulative-member-count">
          <p class="text-title">累計入会数</p>
          <p class="text-value">{{ numberFormat(planPrice?.cumulativeMembershipCount) }}</p>
        </div>
        <div class="col-withdraw-member-count">
          <p class="text-title">退会数</p>
          <p class="text-value">{{ numberFormat(planPrice?.withdrawMembershipCount) }}</p>
        </div>
        <div class="col-active-member-count">
          <p class="text-title">入会中</p>
          <p class="text-value">{{ numberFormat(planPrice?.activeMembershipCount) }}</p>
        </div>
        <div class="col-hidden">
          <p class="text-title">非表示</p>
          <p class="text-value">{{ planPrice?.displayFlag ? "-" : "非表示" }}</p>
        </div>
      </v-sheet>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .columns {
    display: flex;
    align-items: center;
    text-align: center;
    min-width: max-content;
  }
  .columns-sp {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: flex-start;
    text-align: center;
  }

  .col-handle {
    min-width: 60px;
    max-width: 60px;
  }

  .col-handle-sp {
    min-width: 40px;
    max-width: 40px;
  }
  .col-name {
    min-width: 200px;
    max-width: 200px;
    text-align: left;
  }
  .col-name-sp {
    min-width: 180px;
    flex-grow: 1;
  }
  .col-price-id-sp {
    min-width: 80px;
    max-width: 80px;
  }
  .col-billing-type {
    min-width: 160px;
    max-width: 160px;
    text-align: left;
  }
  .col-billing-type-sp {
    min-width: 120px;
    max-width: 160px;
  }
  .col-price {
    min-width: 80px;
    max-width: 80px;
  }
  .col-start-end-date {
    min-width: 140px;
    max-width: 140px;
  }
  .col-trial-days {
    min-width: 80px;
    max-width: 80px;
  }
  .col-cumulative-member-count {
    min-width: 80px;
    max-width: 80px;
  }
  .col-withdraw-member-count {
    min-width: 80px;
    max-width: 80px;
  }
  .col-active-member-count {
    min-width: 80px;
    max-width: 80px;
  }
  .col-hidden {
    min-width: 80px;
    max-width: 80px;
  }
  .col-operation {
    min-width: 40px;
    max-width: 40px;
  }

  .text-title {
    font-size: 12px;
    color: gray;
    font-weight: 400;
    line-height: 1.8em;
  }

  .text-value {
    font-size: 14px;
    font-weight: 400;
    line-height: 1.8em;
  }

  .text-value-s {
    font-size: 14px;
    font-weight: 400;
    line-height: 1.8em;
  }

  .popup {
    background-color: rgb(var(--v-theme-monochrome-04)) !important;
  }
  .popup-list {
    padding: 8px 0;
  }
  .popup-list-item {
    padding: 8px 12px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    &:hover {
      background-color: rgb(var(--v-theme-monochrome-05));
    }
  }
  .popup-list-item-disabled {
    padding: 8px 12px;
    cursor: not-allowed;
    display: flex;
    justify-content: space-between;
  }
  .popup-text {
    color: white;
    &.disabled {
      color: rgb(var(--v-theme-monochrome-03));
    }
  }
  .handle {
    cursor: move;
  }
</style>
